import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Download, Info, Printer } from 'lucide-react';
import type { Statement, StatementLine } from '@portal-contract';
import { Alert, AlertDescription, TableBase, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/lib/macondo';
import { Btn } from '@/components/Btn';
import { useI18n } from '@/i18n/I18nProvider';
import { api } from '@/api/client';
import { useStatement } from '@/api/queries';
import { useIsWide } from '@/hooks/useMediaQuery';
import { usePdfDownload } from '@/hooks/usePdfDownload';
import { firstOfMonth, formatAmount, formatDate, formatDateTime, lastOfMonth, parseDay, slug, toDay } from '@/lib/format';
import { PageHeading } from '@/components/PageHeading';
import { Card, Section } from '@/components/Card';
import { AgingTable } from '@/components/AgingTable';
import { EmptyState, ErrorState, LoadingState } from '@/components/States';
import { Field } from '@/components/Field';
import { ErrorMessage } from '@/components/ErrorMessage';
import type { MessageKey } from '@/i18n/translate';

type Preset = 'thisMonth' | 'lastMonth' | 'last3' | 'ytd' | 'custom';
interface Period {
  preset: Preset;
  /** undefined with preset last3 = the server default period. */
  from?: string;
  to?: string;
}
const PRESETS: Array<{ value: Preset; key: MessageKey }> = [
  { value: 'thisMonth', key: 'statement.preset.thisMonth' },
  { value: 'lastMonth', key: 'statement.preset.lastMonth' },
  { value: 'last3', key: 'statement.preset.last3' },
  { value: 'ytd', key: 'statement.preset.ytd' },
  { value: 'custom', key: 'statement.preset.custom' },
];

/** `/a/:cid/statement`: period presets, the statement table, PDF download and print. */
export function StatementScreen() {
  const { cid = '' } = useParams();
  const { t } = useI18n();
  const [period, setPeriod] = useState<Period>({ preset: 'last3' });
  // "Today" comes from the server (the `to` of the default period), never from this device's clock.
  const [today, setToday] = useState<string | null>(null);
  const q = useStatement(cid, period.from, period.to);
  useEffect(() => {
    if (!today && period.preset === 'last3' && q.data && !q.isPlaceholderData) setToday(q.data.to);
  }, [today, period.preset, q.data, q.isPlaceholderData]);

  const historyFrom = q.data?.historyFrom;
  const clamp = (from: string) => (historyFrom && from < historyFrom ? historyFrom : from);

  function choose(preset: Preset) {
    if (preset === 'custom') {
      setPeriod({ preset: 'custom', ...(q.data ? { from: q.data.from, to: q.data.to } : {}) });
      return;
    }
    if (preset === 'last3') return setPeriod({ preset });
    if (!today) return;
    if (preset === 'thisMonth') setPeriod({ preset, from: clamp(firstOfMonth(today)), to: today });
    else if (preset === 'lastMonth') setPeriod({ preset, from: clamp(firstOfMonth(today, -1)), to: lastOfMonth(today, -1) });
    else setPeriod({ preset, from: clamp(toDay(parseDay(today)!.y, 1, 1)), to: today });
  }

  return (
    <div className="flex flex-col gap-500" data-screen="StatementScreen">
      <PageHeading>{t('statement.heading')}</PageHeading>

      <div className="print:hidden">
        <div role="group" aria-label={t('statement.period')} className="flex flex-wrap gap-200">
          {PRESETS.map((p) => (
            <Btn
              key={p.value}
              type="button"
              variant={period.preset === p.value ? 'default' : 'secondary'}
              size="lg"
              aria-pressed={period.preset === p.value}
              disabled={p.value !== 'last3' && p.value !== 'custom' && !today}
              className="min-h-[44px]"
              onClick={() => choose(p.value)}
            >
              {t(p.key)}
            </Btn>
          ))}
        </div>
        {period.preset === 'custom' && (
          <CustomPeriod
            key={`${period.from ?? ''}${period.to ?? ''}`}
            initial={{ from: period.from ?? '', to: period.to ?? '' }}
            historyFrom={historyFrom}
            today={today}
            onApply={(from, to) => setPeriod({ preset: 'custom', from, to })}
          />
        )}
      </div>

      {q.isPending ? (
        <LoadingState rows={5} />
      ) : q.isError && !q.data ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : q.data ? (
        <div aria-busy={q.isPlaceholderData} className={q.isPlaceholderData ? 'opacity-60' : ''}>
          <Loaded cid={cid} data={q.data} />
        </div>
      ) : null}
    </div>
  );
}

function CustomPeriod({
  initial,
  historyFrom,
  today,
  onApply,
}: {
  initial: { from: string; to: string };
  historyFrom: string | undefined;
  today: string | null;
  onApply: (from: string, to: string) => void;
}) {
  const { t, lang } = useI18n();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [error, setError] = useState<string | null>(null);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!from || !to || from > to) return setError(t('statement.invalidPeriod'));
    if (historyFrom && from < historyFrom) return setError(t('statement.historyLimit', { date: formatDate(historyFrom, lang) }));
    if (today && to > today) return setError(t('statement.invalidPeriod'));
    setError(null);
    onApply(from, to);
  }

  return (
    <form onSubmit={submit} noValidate className="mt-400 grid grid-cols-1 items-start gap-300 sm:grid-cols-[1fr_1fr_auto]">
      <Field label={t('invoices.from')} type="date" name="from" value={from} min={historyFrom} max={to || today || undefined} onChange={(e) => setFrom(e.target.value)} />
      <Field label={t('invoices.to')} type="date" name="to" value={to} min={from || historyFrom} max={today || undefined} onChange={(e) => setTo(e.target.value)} />
      <div className="sm:pt-[28px]">
        <Btn type="submit" size="lg" className="min-h-[48px] w-full">
          {t('statement.apply')}
        </Btn>
      </div>
      <p className="text-footnote text-typography-secondary sm:col-span-3" aria-live="polite">
        {error ?? (historyFrom ? t('statement.historyLimit', { date: formatDate(historyFrom, lang) }) : '')}
      </p>
    </form>
  );
}

function Loaded({ cid, data }: { cid: string; data: Statement }) {
  const { t, lang } = useI18n();
  const pdf = usePdfDownload();
  const money = (n: number) => formatAmount(n, data.currency, lang);
  const filename = `statement-${slug(data.customer.name)}-${data.from}-${data.to}.pdf`;
  return (
    <div className="flex flex-col gap-500">
      <div className="flex flex-wrap items-start justify-between gap-300">
        <div>
          <p className="text-headline text-typography-primary break-words">{data.customer.name}</p>
          <p className="text-callout text-typography-secondary mt-050">
            {t('statement.period')}: {formatDate(data.from, lang)} - {formatDate(data.to, lang)}
          </p>
          <p className="text-callout text-typography-secondary break-words">
            {data.customer.billTo.map((l, i) => (
              <span key={i} className="block">
                {l}
              </span>
            ))}
          </p>
        </div>
        <div className="flex flex-wrap gap-300 print:hidden">
          <Btn
            type="button"
            variant="secondary"
            size="lg"
            className="min-h-[48px]"
            loading={pdf.pending}
            disabled={pdf.pending}
            onClick={() => void pdf.run(() => api.statementPdf(cid, { from: data.from, to: data.to, lang }), filename)}
          >
            <Download size={18} aria-hidden="true" />
            {t('common.download')}
          </Btn>
          <Btn type="button" variant="secondary" size="lg" className="min-h-[48px]" onClick={() => window.print()}>
            <Printer size={18} aria-hidden="true" />
            {t('common.print')}
          </Btn>
        </div>
      </div>
      {pdf.error ? <ErrorMessage error={pdf.error} /> : null}

      {!data.reliable && (
        <Alert tone="info" role="note">
          <div className="flex items-start gap-300">
            <Info size={20} aria-hidden="true" className="mt-050 shrink-0" />
            <AlertDescription>{t('statement.unreliable')}</AlertDescription>
          </div>
        </Alert>
      )}

      <dl className={`grid gap-200 grid-cols-2 ${data.reliable ? 'lg:grid-cols-4' : ''}`}>
        {data.reliable && data.openingBalance !== null && <Figure label={t('statement.opening')} value={money(data.openingBalance)} />}
        <Figure label={t('statement.charges')} value={money(data.totalCharges)} />
        <Figure label={t('statement.credits')} value={money(data.totalCredits)} />
        {data.reliable && data.closingBalance !== null && <Figure label={t('statement.closing')} value={money(data.closingBalance)} strong />}
      </dl>

      {data.lines.length === 0 ? (
        <EmptyState>{t('empty.statement')}</EmptyState>
      ) : (
        <Lines cid={cid} data={data} money={money} />
      )}

      <Section id="stmt-aging-h" title={t('overview.aging')}>
        <Card className="p-500">
          <AgingTable aging={data.aging} currency={data.currency} id="stmt-aging-h" />
        </Card>
      </Section>

      <p className="text-footnote text-typography-secondary">
        {t('common.dataAsOf', { time: formatDateTime(data.dataAsOf, lang) })}
      </p>
    </div>
  );
}

function Figure({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Card className="min-w-0 p-400">
      <dt className="text-callout text-typography-secondary">{label}</dt>
      <dd className={`${strong ? 'text-headline-medium font-semibold' : 'text-headline'} text-typography-primary mt-100 tabular-nums break-words`}>{value}</dd>
    </Card>
  );
}

const KIND_KEY: Record<StatementLine['kind'], MessageKey> = {
  invoice: 'statement.kind.invoice',
  payment: 'statement.kind.payment',
  credit: 'statement.kind.credit',
};

function Lines({ cid, data, money }: { cid: string; data: Statement; money: (n: number) => string }) {
  const { t, lang } = useI18n();
  const wide = useIsWide();
  const ref = (l: StatementLine) =>
    l.invoiceId ? (
      <Link to={`/a/${encodeURIComponent(cid)}/invoices/${encodeURIComponent(l.invoiceId)}`} className="focus-ring text-typography-primary underline">
        {l.reference}
      </Link>
    ) : (
      <>{l.reference || '-'}</>
    );

  if (wide) {
    return (
      <Card className="overflow-hidden">
        <TableBase>
          <TableCaption className="sr-only">{t('statement.heading')}</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">{t('statement.col.date')}</TableHead>
              <TableHead scope="col">{t('statement.col.type')}</TableHead>
              <TableHead scope="col">{t('statement.col.reference')}</TableHead>
              <TableHead scope="col" className="text-right">
                {t('statement.col.charge')}
              </TableHead>
              <TableHead scope="col" className="text-right">
                {t('statement.col.credit')}
              </TableHead>
              {data.reliable && (
                <TableHead scope="col" className="text-right">
                  {t('statement.col.balance')}
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.lines.map((l, i) => (
              <TableRow key={i}>
                <TableCell>{formatDate(l.date, lang)}</TableCell>
                <TableCell>{t(KIND_KEY[l.kind])}</TableCell>
                <TableCell>{ref(l)}</TableCell>
                <TableCell className="text-right tabular-nums">{l.charge === null ? '' : money(l.charge)}</TableCell>
                <TableCell className="text-right tabular-nums">{l.credit === null ? '' : money(l.credit)}</TableCell>
                {data.reliable && <TableCell className="text-right font-medium tabular-nums">{l.runningBalance === null ? '' : money(l.runningBalance)}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </TableBase>
      </Card>
    );
  }

  return (
    <ul className="flex flex-col gap-200" aria-label={t('statement.heading')}>
      {data.lines.map((l, i) => (
        <li key={i}>
          <Card className="p-400">
            <div className="flex items-baseline justify-between gap-300">
              <span className="text-callout-semibold text-typography-primary">{t(KIND_KEY[l.kind])}</span>
              <span className="text-footnote text-typography-secondary">{formatDate(l.date, lang)}</span>
            </div>
            <p className="text-callout text-typography-secondary break-words">{ref(l)}</p>
            <dl className="text-callout mt-200 grid grid-cols-2 gap-x-400 gap-y-100">
              {l.charge !== null && (
                <div>
                  <dt className="text-footnote text-typography-secondary">{t('statement.col.charge')}</dt>
                  <dd className="tabular-nums">{money(l.charge)}</dd>
                </div>
              )}
              {l.credit !== null && (
                <div>
                  <dt className="text-footnote text-typography-secondary">{t('statement.col.credit')}</dt>
                  <dd className="tabular-nums">{money(l.credit)}</dd>
                </div>
              )}
              {data.reliable && l.runningBalance !== null && (
                <div>
                  <dt className="text-footnote text-typography-secondary">{t('statement.col.balance')}</dt>
                  <dd className="text-callout-semibold tabular-nums">{money(l.runningBalance)}</dd>
                </div>
              )}
            </dl>
          </Card>
        </li>
      ))}
    </ul>
  );
}
